<template>
  <AppDialog
    v-model="dialog"
    :title='whale.id ? "Edit Whale" : "Create Whale"'
    max-width="1000px"
    @close="close"
  >
    <v-card-text>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-container>
          <v-row>
            <v-col cols="4">
              <div class="profile-photo-block">
                <v-img
                  v-if="featurePhotoUrl"
                  :src="featurePhotoUrl"
                  width="240"
                  height="240"
                  cover
                  class="bg-grey-lighten-2 mb-2"
                />
                <div
                  v-else
                  class="profile-photo-placeholder mb-2"
                >
                  <v-icon size="64" color="grey">mdi-whale</v-icon>
                </div>
                <v-btn
                  variant="outlined"
                  @click="openPicker"
                >
                  <v-icon start>mdi-image</v-icon>
                  {{ featurePhotoUrl ? "Change Photo" : "Choose Photo" }}
                </v-btn>
              </div>
            </v-col>
            <v-col cols="8">
              <v-row>
                <v-col cols="2">
                  <v-text-field
                    v-model="whale.whaleId"
                    name="whaleid"
                    label="Whale Id"
                    required
                  />
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    v-model="whale.whaleName"
                    name="name"
                    label="Whale Name"
                    required
                  />
                </v-col>
                <v-col cols="3">
                  <v-select
                      v-model="whale.species"
                      name="species"
                      label="Species"
                      required
                      :items="Object.values(enums.sightingSpecies)"
                  />
                </v-col>
                <v-col cols="3">
                  <v-text-field
                      v-model="whale.birthYear"
                      name="birthyear"
                      label="Birth Year"
                      type="number"
                      hide-spin-buttons
                  />
                </v-col>
              </v-row>
              <v-row v-if="isOrca">
                <v-col cols="3">
                  <v-select
                      v-model="whale.ecotype"
                      name="ecotype"
                      label="Ecotype"
                      :items="enums.ecotype"
                      :disabled="ecotypeLocked"
                  />
                </v-col>
                <v-col cols="3">
                  <v-select
                      v-model="whale.clan"
                      name="clan"
                      label="Clan"
                      :items="clanItems"
                      :disabled="!clanEnabled"
                  />
                </v-col>
                <v-col cols="3">
                  <v-select
                      v-model="whale.pod"
                      name="pod"
                      label="Pod"
                      :items="podItems"
                      :disabled="!whale.clan"
                  />
                </v-col>
                <v-col cols="3">
                  <v-text-field
                      v-model="whale.matriline"
                      name="matriline"
                      label="Matriline"
                  />
                </v-col>
              </v-row>
              <v-row v-if="isOrca">
                <v-col cols="3">
                  <v-combobox
                      v-model="whale.mother"
                      name="mother"
                      label="Mother (whale ID)"
                      :items="motherOptions"
                      clearable
                  />
                </v-col>
                <v-col cols="3">
                  <v-select
                      v-model="whale.maternalConfidence"
                      name="maternal_confidence"
                      label="Maternal confidence"
                      :items="enums.maternalConfidence"
                      :disabled="!whale.mother"
                      clearable
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-if="whale.id">
            <v-col cols="12">
              <h4 class="text-h6 mb-2">
                Sightings
              </h4>
              <v-data-table
                :headers="sightingHeaders"
                :items="sightings"
                :loading="loadingSightings"
                density="compact"
                :items-per-page="10"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <b class="red-text"> {{ message }}</b>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn @click="close">
        <v-icon>mdi-close</v-icon>
        Cancel
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        :disabled="saving"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        <span v-show="!saving">{{ button }}</span>
        <pulse-loader :loading="saving" />
      </v-btn>
    </v-card-actions>
  </AppDialog>
  <v-dialog
    v-model="contradictionDialog"
    persistent
    max-width="480px"
  >
    <v-card>
      <v-card-title class="text-h6">
        Inconsistent Whale Data
      </v-card-title>
      <v-card-text>
        {{ contradiction ? contradiction.message : "" }}
      </v-card-text>
      <v-card-actions class="d-flex flex-column align-stretch">
        <v-btn
          v-for="(option, index) in contradictionOptions"
          :key="index"
          color="primary"
          class="mb-2"
          @click="applyContradictionOption(option)"
        >
          {{ option.label }}
        </v-btn>
        <v-btn
          class="mb-2"
          @click="keepContradictionAsIs"
        >
          Keep as-is
        </v-btn>
        <v-btn @click="cancelContradiction">
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <feature-photo-picker
    v-model="pickerOpen"
    :whale-id="whale.whaleId || ''"
    :current-image-id="whale.featurePhotoId || null"
    @pick="onPhotoPicked"
  />
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AppDialog from "@/components/AppDialog";
import PulseLoader from "vue-spinner/src/PulseLoader";
import enums from "@/helpers/enums";
import orcaTaxonomy from "@/helpers/orcaTaxonomy";
import FeaturePhotoPicker from "@/components/whale/FeaturePhotoPicker.vue";

/**
 * species: str
 *     whale_id: Optional[str]
 *     whale_name: Optional[str]
 *     clan: Optional[str]
 *     pod: Optional[str]
 *     matriline: Optional[str]
 *     birth_year: Optional[int]
 *
 *     behaviours: Optional[str]
 *     features: Optional[str]
 */
export default {
  components: {
    AppDialog,
    PulseLoader,
    FeaturePhotoPicker,
  },

  data() {
    return {
      valid: true,
      whale: {},
      contradictionDialog: false,
      contradiction: null,
      pickerOpen: false,
      sightingHeaders: [
        { title: "Sighting Number", sortable: true, key: "sightingNumber" },
        { title: "Date", sortable: true, key: "sightingDate" },
        { title: "Species", sortable: true, key: "species" },
        { title: "Location", sortable: true, key: "locationDescription" },
        { title: "Behaviours", sortable: false, key: "bhv" },
      ],
    };
  },
  computed: {
    enums() {
      return enums;
    },
    isOrca() {
      return orcaTaxonomy.isOrca(this.whale.species);
    },
    ecotypeLocked() {
      return orcaTaxonomy.ecotypeIsLocked(this.whale.species);
    },
    clanEnabled() {
      return orcaTaxonomy.clanEnabled(this.whale.species, this.whale.ecotype);
    },
    clanItems() {
      return orcaTaxonomy.clanOptions(this.whale.species, this.whale.ecotype);
    },
    podItems() {
      return orcaTaxonomy.podOptions(this.whale.clan);
    },
    motherOptions() {
      return (this.whaleIds || []).filter((id) => id !== this.whale.whaleId);
    },
    contradictionOptions() {
      return this.contradiction ? this.contradiction.options : [];
    },
    featurePhotoUrl() {
      const photo = this.whale.featurePhoto;
      const url = photo && photo.thumbnailUrl;
      return this.resolveStorageUrl(url);
    },
    ...mapGetters({
      saving: "whale/details/saving",
      saved: "whale/details/saved",
      whaleToEdit: "whale/details/whale",
      dialog: "whale/details/dialog",
      button: "whale/details/button",
      title: "whale/details/title",
      message: "whale/details/message",
      sightings: "whale/details/sightings",
      loadingSightings: "whale/details/loadingSightings",
      whaleIds: "whales/whaleIds",
    }),
  },
  watch: {
    dialog(dialog) {
      if (dialog) {
        this.whale = {};
        this.contradiction = null;
        this.contradictionDialog = false;
        this.pickerOpen = false;
        this.loadWhaleIds();
        if (this.whaleToEdit) {
          this.whale = { ...this.whaleToEdit };
          this.checkContradiction();
          if (this.whale.id && this.whale.whaleId) {
            this.loadSightings(this.whale.whaleId);
          }
        }
      }
    },
    "whale.species"() {
      this.applyForcedEcotype();
    },
    "whale.ecotype"() {
      if (!this.clanEnabled) {
        this.whale.clan = "";
        this.whale.pod = "";
      }
    },
    "whale.clan"(clan) {
      if (!clan) {
        this.whale.pod = "";
      } else if (this.whale.pod && !this.podItems.includes(this.whale.pod)) {
        this.whale.pod = "";
      }
    },
  },
  methods: {
    ...mapActions({
      update: "whale/details/update",
      create: "whale/details/create",
      close: "whale/details/close",
      setMessage: "whale/details/setMessage",
      loadSightings: "whale/details/loadSightings",
      setFeaturePhoto: "whale/details/setFeaturePhoto",
      loadWhaleIds: "whales/loadWhaleIds",
    }),
    resolveStorageUrl(url) {
      if (!url) return "";
      if (url.startsWith("http")) return url;
      return `${process.env.VUE_APP_API_URL}${url}`;
    },
    applyForcedEcotype() {
      const forced = orcaTaxonomy.forcedEcotype(this.whale.species);
      if (forced) {
        this.whale.ecotype = forced;
      }
    },
    checkContradiction() {
      const found = orcaTaxonomy.findContradiction(this.whale);
      if (found) {
        this.contradiction = found;
        this.contradictionDialog = true;
      } else {
        this.contradiction = null;
        this.contradictionDialog = false;
      }
    },
    applyContradictionOption(option) {
      this.whale = { ...this.whale, ...option.changes };
      this.contradictionDialog = false;
      this.contradiction = null;
      this.$nextTick(() => {
        this.checkContradiction();
      });
    },
    keepContradictionAsIs() {
      this.contradictionDialog = false;
      this.contradiction = null;
    },
    cancelContradiction() {
      this.contradictionDialog = false;
      this.contradiction = null;
      this.close();
    },
    openPicker() {
      this.pickerOpen = true;
    },
    async onPhotoPicked(image) {
      if (this.whaleToEdit && this.whale.id) {
        await this.setFeaturePhoto({ whaleId: this.whale.id, imageId: image.id });
        if (this.whaleToEdit) {
          this.whale.featurePhotoId = this.whaleToEdit.featurePhotoId;
          this.whale.featurePhoto = this.whaleToEdit.featurePhoto;
        }
      } else {
        this.whale.featurePhotoId = image.id;
        this.whale.featurePhoto = image;
      }
    },
    async save() {
      await this.$refs.form.validate();
      if (!this.valid) {
        return;
      }
      if (!this.whale.whaleName) {
        this.setMessage("The whale must have a whale name!");
        return;
      }
      if (!this.whale.whaleId) {
        this.setMessage("The whale must have a whale id!");
        return;
      }
      const payload = { ...this.whale };
      if (!this.isOrca) {
        delete payload.ecotype;
        delete payload.clan;
        delete payload.pod;
        delete payload.matriline;
      }
      if (payload && payload.id) {
        this.update(payload);
      } else {
        await this.create({
          ...payload,
          featurePhotoId: this.whale.featurePhotoId || null,
        });
      }
    },
  },
};
</script>

<style>

.profile-photo-placeholder {
  width: 240px;
  height: 240px;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
